<div>
  <div class="flex justify-between items-center mb-4">
    <div class="flex-1">
      <h1 class="text-gray-900 m-0 mb-1 text-2xl font-semibold">Employees</h1>
      <p class="text-gray-500 m-0 text-lg">Invite and manage employees and their roles.</p>
    </div>
    <div class="flex gap-2 items-center">
      <p-iconfield iconPosition="left">
        <p-inputicon class="pi pi-search"></p-inputicon>
        <input
          type="text"
          pInputText
          placeholder="Search employees..."
          [value]="filters.search"
          (input)="onSearchChange($any($event.target).value)" />
      </p-iconfield>
      @if (canManageTeam) {
        <p-button
          label="Invite Employee"
          icon="pi pi-send"
          (onClick)="openAddModal()">
        </p-button>
      }
      @if (canManageTeam) {
        <p-button
          label="Bulk Invite"
          icon="pi pi-users"
          severity="secondary"
          (onClick)="openBulkInviteModal()">
        </p-button>
      }
    </div>
  </div>

  <!-- Employees Table -->
  @if (!loading) {
    <div>
      <p-table
        [value]="processedUsers"
        [loading]="loading">
        <ng-template #header>
          <tr>
            <th>Name</th>
            <th>Email</th>
            <th>Role</th>
            <th>Position</th>
            <th>Status</th>
            <th>Created</th>
            @if (canManageTeam) {
              <th>Actions</th>
            }
          </tr>
        </ng-template>
        <ng-template #body let-user>
          <tr>
            <td>{{ user.fullName || '-' }}</td>
            <td>{{ user.email || '-' }}</td>
            <td>
              <p-badge
                [value]="user.roleName"
                [severity]="getBadgeSeverity(user.roleName)">
              </p-badge>
            </td>
            <td>{{ user.position || '-' }}</td>
            <td>
              <p-badge
                [value]="user.status"
                [severity]="getBadgeSeverity(user.status)">
              </p-badge>
            </td>
            <td>{{ user.createdAt || '-' }}</td>
            @if (canManageTeam) {
              <td>
                <div class="flex gap-2">
                  <!-- Invite actions -->
                  @if (user.isInvite) {
                    <p-button
                      icon="pi pi-replay"
                      severity="secondary"
                      variant="text"
                      pTooltip="Resend Invite"
                      (onClick)="resendInvite(user)"
                      aria-label="Resend invite">
                    </p-button>
                    <p-button
                      icon="pi pi-times"
                      severity="danger"
                      variant="text"
                      pTooltip="Cancel Invite"
                      (onClick)="cancelInvite(user)"
                      aria-label="Cancel invite">
                    </p-button>
                  } @else {
                    <p-button
                      icon="pi pi-pencil"
                      severity="secondary"
                      variant="text"
                      pTooltip="Edit User"
                      (onClick)="openEditModal(user.originalUser)"
                      aria-label="Edit user">
                    </p-button>
                    @if (!isUserProtected(user.originalUser)) {
                      <p-button
                        icon="pi pi-trash"
                        severity="danger"
                        variant="text"
                        pTooltip="Delete User"
                        (onClick)="openDeleteModal(user.originalUser)"
                        aria-label="Delete user">
                      </p-button>
                    }
                    @if (user.status === 'Active' && !isUserProtected(user.originalUser)) {
                      <p-button
                        icon="pi pi-lock-open"
                        severity="success"
                        variant="text"
                        pTooltip="Freeze Account"
                        (onClick)="toggleUserStatus(user.originalUser, UserStatus.Inactive)"
                        aria-label="Freeze user">
                      </p-button>
                    }
                    @if (user.status === 'Inactive' && !isUserProtected(user.originalUser)) {
                      <p-button
                        icon="pi pi-lock"
                        severity="danger"
                        variant="text"
                        pTooltip="Activate Account"
                        (onClick)="toggleUserStatus(user.originalUser, UserStatus.Active)"
                        aria-label="Unfreeze user">
                      </p-button>
                    }
                  }
                  <!-- User actions -->
                </div>
              </td>
            }
          </tr>
        </ng-template>
        <ng-template #emptymessage>
          <tr>
            <td [attr.colspan]="canManageTeam ? 7 : 6" class="text-center">
              <div class="p-4">
                <i class="pi pi-users text-4xl text-gray-400 mb-3"></i>
                <h3 class="text-gray-600 mb-2">No Employees Found</h3>
                @if (filters.search) {
                  <p class="text-gray-500">
                    No employees match your search criteria. Try adjusting your search terms.
                  </p>
                } @else {
                  <p class="text-gray-500">Create your first employee to get started</p>
                }
              </div>
            </td>
          </tr>
        </ng-template>
      </p-table>
    </div>
  } @else {
    <div class="flex justify-center items-center p-4">
      <p-progress-spinner></p-progress-spinner>
    </div>
  }

</div>

<!-- Add User Modal -->
<app-user-invite-modal
  [(visible)]="showAddModal"
  [formData]="formData"
  [roleOptions]="getFormRoleOptions()"
  [formErrors]="formErrors"
  [loading]="loading"
  (emailChange)="onEmailChange($event)"
  (submitted)="saveUser()"
  (cancelled)="closeModals()">
</app-user-invite-modal>

<!-- Edit User Modal -->
<app-user-edit-modal
  [(visible)]="showEditModal"
  [formData]="formData"
  [roleOptions]="getFormRoleOptions()"
  [formErrors]="formErrors"
  [canEditRole]="canEditRole()"
  [loading]="loading"
  (submitted)="saveUser()"
  (cancelled)="closeModals()">
</app-user-edit-modal>

<!-- Delete User Modal -->
<p-dialog
  header="Delete Employee"
  [(visible)]="showDeleteModal"
  [modal]="true"
  [closable]="true"
  [draggable]="false"
  [resizable]="false"
  [baseZIndex]="1000000"
  styleClass="w-full max-w-md">

  <div class="flex flex-col items-center text-center gap-4">
    <i class="pi pi-exclamation-triangle text-red-500 text-5xl"></i>
    <div class="flex flex-col gap-2">
      <p class="text-lg font-medium">Are you sure you want to delete <strong>{{ selectedUser?.fullName || selectedUser?.email }}</strong>?</p>
      <p class="text-gray-600">This action is permanent and cannot be undone.</p>
    </div>
    <div class="flex justify-end gap-2 pt-4">
      <p-button
        label="Cancel"
        severity="secondary"
        (onClick)="closeModals()">
      </p-button>
      <p-button
        label="Delete Employee"
        severity="danger"
        [loading]="loading"
        (onClick)="deleteUser()"
        icon="pi pi-trash">
      </p-button>
    </div>
  </div>
</p-dialog>

<!-- Bulk Invite Modal -->
<app-user-bulk-invite-modal
  [(visible)]="showBulkInviteModal"
  [roleOptions]="getFormRoleOptions()"
  [loading]="loading"
  (send)="sendBulkInvites($event)">
</app-user-bulk-invite-modal>
